import ReactGA from "react-ga4";
import {
  ANALYTICS_STORAGE_KEYS,
  DEDUPE_WINDOW_MS,
  resolveEnvironment,
  resolveMeasurementId,
  isLocalDevelopment,
  isApiTrackingEnabled,
  isErrorTrackingEnabled,
} from "./constants";
import {
  resolvePageCategory,
  resolvePageTitle,
  sanitizePageLocation,
} from "./utils/resolvePageName";
import { resolveDashboardDimension } from "./utils/resolveDashboardDimension";
import {
  claimInteraction,
  normalizeInteractionParams,
} from "./utils/interactionMetadata";

const safeNow = () => Date.now();

const createSessionId = () => {
  if (globalThis.crypto?.randomUUID) {
    return `s_${globalThis.crypto.randomUUID()}`;
  }

  if (globalThis.crypto?.getRandomValues) {
    const randomValues = globalThis.crypto.getRandomValues(new Uint32Array(2));
    return `s_${randomValues[0].toString(36)}${randomValues[1].toString(36)}`;
  }

  return `s_${safeNow()}`;
};

const getOrCreateSessionId = () => {
  const stored = sessionStorage.getItem(ANALYTICS_STORAGE_KEYS.SESSION_ID);
  if (stored) return stored;

  const id = createSessionId();
  sessionStorage.setItem(ANALYTICS_STORAGE_KEYS.SESSION_ID, id);
  sessionStorage.setItem(
    ANALYTICS_STORAGE_KEYS.SESSION_START,
    String(safeNow()),
  );
  return id;
};

const stableStringify = (value) => {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;

  return `{${Object.keys(value)
    .sort((left, right) => left.localeCompare(right))
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

const compactObject = (value) => {
  if (value === undefined || value === null) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => {
      return (
        entryValue !== null && entryValue !== undefined && entryValue !== ""
      );
    }),
  );
};

const SENSITIVE_PARAM_KEYS = new Set([
  "access_token",
  "authorization",
  "account_number",
  "address",
  "balance",
  "bank_name",
  "card_number",
  "customer_name",
  "cvv",
  "email",
  "financial_data",
  "first_name",
  "full_name",
  "input_value",
  "institution_id",
  "institution_name",
  "last_name",
  "name",
  "password",
  "payment_details",
  "payment_method",
  "phone",
  "phone_number",
  "reason_value",
  "session_id",
  "ssn",
  "stack_trace",
  "tax_id",
  // Sent only as GA4 user properties via setUser(), never as event parameters.
  "user_id",
  "user_name",
  "username",
  "click_value",
  "organization_name",
]);

const USER_PROPERTY_KEYS = [
  "user_id",
  "user_name",
  "user_role",
  "subscription_plan",
];

const isEmailAddress = (value) => {
  const atIndex = value.indexOf("@");
  if (atIndex <= 0 || atIndex !== value.lastIndexOf("@") || /\s/.test(value)) {
    return false;
  }

  const domainDotIndex = value.indexOf(".", atIndex + 1);
  return domainDotIndex > atIndex + 1 && domainDotIndex < value.length - 1;
};

const sanitizeAnalyticsValue = (value, eventName) => {
  if (typeof value === "string") {
    return isEmailAddress(value) ? undefined : value;
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeAnalyticsValue(entry, eventName))
      .filter((entry) => entry !== undefined);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => {
          const normalizedKey = key.toLowerCase();
          return (
            !SENSITIVE_PARAM_KEYS.has(normalizedKey) &&
            (normalizedKey !== "value" || eventName === "purchase")
          );
        })
        .map(([key, entry]) => [key, sanitizeAnalyticsValue(entry, eventName)])
        .filter(([, entry]) => entry !== undefined),
    );
  }

  return value;
};

const sanitizePayload = (payload, eventName) => {
  delete payload.page_path;
  delete payload.trigger_element_id;
  payload.page_location = sanitizePageLocation(payload.page_location);
  ["page_referrer", "previous_page_location", "next_page_location"].forEach(
    (key) => {
      if (payload[key]) payload[key] = sanitizePageLocation(payload[key]);
    },
  );
  return sanitizeAnalyticsValue(payload, eventName);
};

class AnalyticsService {
  initialized = false;

  enabled = false;

  queue = [];

  maxRetries = 2;

  retryBackoffMs = 300;

  context = {};

  lastEventKey = null;

  lastEventAt = 0;

  sessionId = getOrCreateSessionId();

  measurementId = "";

  constructor() {
    globalThis.addEventListener?.("online", () => this.flushQueue());
  }

  init(config = {}) {
    if (this.initialized) return;

    const measurementId = config.measurementId || resolveMeasurementId();
    const environment = config.environment || resolveEnvironment();
    const localDisable =
      config.disableOnLocal !== false && isLocalDevelopment();
    this.enabled = !localDisable && Boolean(measurementId);
    this.measurementId = measurementId;
    this.context.environment = environment;
    this.context.application_version =
      process.env.REACT_APP_VERSION || "unknown";

    if (!this.enabled) {
      this.initialized = true;
      return;
    }

    ReactGA.initialize(measurementId, {
      gtagOptions: {
        send_page_view: false,
        anonymize_ip: true,
      },
    });

    ReactGA.set({
      app_version: this.context.application_version,
      environment,
    });
    // Set for every visitor (logged in or not); clearUser never resets it.
    if (typeof ReactGA.gtag === "function") {
      ReactGA.gtag("set", "user_properties", { environment });
    }

    this.initialized = true;
    this.flushQueue();
  }

  setUser(userId, properties = {}) {
    if (!userId) return;

    const safeProperties = compactObject(
      Object.fromEntries(
        Object.entries(properties).filter(([key]) =>
          USER_PROPERTY_KEYS.includes(key),
        ),
      ),
    );

    if (this.enabled && typeof ReactGA.gtag === "function") {
      // Re-running `config` sends a page_view unless explicitly disabled.
      ReactGA.gtag("config", this.measurementId, {
        user_id: String(userId),
        send_page_view: false,
      });
      if (Object.keys(safeProperties).length > 0) {
        ReactGA.gtag("set", "user_properties", safeProperties);
      }
    }
  }

  clearUser() {
    if (!this.enabled || typeof ReactGA.gtag !== "function") return;

    if (this.measurementId) {
      ReactGA.gtag("config", this.measurementId, {
        user_id: null,
        send_page_view: false,
      });
    }
    // gtag merges user_properties, so each key must be explicitly reset.
    ReactGA.gtag(
      "set",
      "user_properties",
      Object.fromEntries(USER_PROPERTY_KEYS.map((key) => [key, null])),
    );
  }

  setContext(partialContext = {}) {
    this.context = {
      ...this.context,
      ...partialContext,
    };
  }

  setLoginTime(value) {
    if (!value) return;
    sessionStorage.setItem(ANALYTICS_STORAGE_KEYS.LOGIN_TIME, String(value));
  }

  getDefaultEventParams() {
    const pagePath =
      `${globalThis?.location?.pathname || ""}${globalThis?.location?.search || ""}` ||
      null;

    return {
      app_version:
        this.context.application_version ||
        process.env.REACT_APP_VERSION ||
        "unknown",
      page_location: sanitizePageLocation(globalThis?.location?.href),
      page_referrer: globalThis.document?.referrer
        ? sanitizePageLocation(globalThis.document.referrer)
        : undefined,
      page_title:
        resolvePageTitle(pagePath) ||
        globalThis.document?.title ||
        "Application Page",
      page_category: resolvePageCategory(pagePath),
    };
  }

  buildEventPayload(params = {}, eventName = "event") {
    const payload = {
      ...this.getDefaultEventParams(),
      ...this.context,
      ...params,
      environment: this.context.environment || resolveEnvironment(),
    };
    if (payload.dashboard != null) {
      payload.dashboard = resolveDashboardDimension(
        payload.dashboard,
        globalThis?.location?.pathname || "",
      );
    }

    const sanitized = sanitizePayload(payload, eventName);
    delete sanitized.item_id;
    if (eventName === "ui_interaction" || eventName === "select_content") {
      const interactionParams = normalizeInteractionParams(sanitized);
      return eventName === "select_content"
        ? {
            ...interactionParams,
            content_type:
              interactionParams.content_type || interactionParams.element_type,
          }
        : interactionParams;
    }
    return sanitized;
  }

  buildPerformancePayload(params = {}, eventName = "performance_metric") {
    const defaults = this.getDefaultEventParams();

    const payload = {
      app_version:
        this.context.application_version ||
        process.env.REACT_APP_VERSION ||
        "unknown",
      page_location: defaults.page_location,
      page_referrer: defaults.page_referrer,
      page_title: defaults.page_title,
      page_category: defaults.page_category,
      dashboard: this.context.dashboard || null,
      ...params,
      environment: this.context.environment || resolveEnvironment(),
    };
    if (payload.dashboard != null) {
      payload.dashboard = resolveDashboardDimension(
        payload.dashboard,
        globalThis?.location?.pathname || "",
      );
    }

    const sanitized = sanitizePayload(payload, eventName);
    delete sanitized.item_id;
    return sanitized;
  }

  buildPayloadForEvent(eventName, params = {}) {
    if (eventName === "performance_metric") {
      return this.buildPerformancePayload(params, eventName);
    }

    return this.buildEventPayload(params, eventName);
  }

  shouldDedupe(eventName, payload) {
    const eventKey = `${eventName}:${stableStringify(payload)}`;
    const now = safeNow();

    if (
      this.lastEventKey === eventKey &&
      now - this.lastEventAt <= DEDUPE_WINDOW_MS
    ) {
      return true;
    }

    this.lastEventKey = eventKey;
    this.lastEventAt = now;
    return false;
  }

  track(eventName, params = {}, options = {}) {
    if (
      (eventName === "api_response" && !isApiTrackingEnabled()) ||
      (eventName === "application_error" && !isErrorTrackingEnabled())
    ) {
      return;
    }

    const isInteraction =
      eventName === "ui_interaction" || eventName === "select_content";
    if (isInteraction && options.nativeInteraction !== false)
      claimInteraction();
    const payload = this.buildPayloadForEvent(eventName, params);
    const eventParams = isInteraction
      ? {
          ...params,
          element_id: payload.element_id,
          element_label: payload.element_label,
          element_type: payload.element_type,
        }
      : params;

    if (options.dedupe !== false && this.shouldDedupe(eventName, payload)) {
      return;
    }

    if (!this.enabled) {
      return;
    }

    if (!this.initialized || !globalThis.navigator?.onLine) {
      this.queue.push({ eventName, params: eventParams, payload, retries: 0 });
      return;
    }

    this.dispatchEvent({ eventName, params: eventParams, payload, retries: 0 });
  }

  dispatchEvent(item) {
    try {
      const payload =
        item.payload || this.buildPayloadForEvent(item.eventName, item.params);

      ReactGA.event(item.eventName, compactObject(payload));
    } catch (error) {
      if (item.retries < this.maxRetries) {
        const retries = item.retries + 1;
        const delay = this.retryBackoffMs * Math.pow(2, retries);
        setTimeout(() => {
          this.dispatchEvent({ ...item, retries });
        }, delay);
        return;
      }

      console.warn("Analytics dispatch failed", error);
    }
  }

  flushQueue() {
    if (!this.initialized || !this.enabled || !globalThis.navigator?.onLine)
      return;

    const pending = [...this.queue];
    this.queue = [];

    pending.forEach((item) => this.dispatchEvent(item));
  }

  // Sync the SPA route into gtag so automatically collected events
  // (user_engagement, scroll, session_start) use the sanitized virtual page.
  setPageContext(params = {}) {
    if (!this.enabled || typeof ReactGA.gtag !== "function") return;

    const payload = this.buildEventPayload(params, "page_view");
    const pageContext = compactObject({
      page_location: payload.page_location,
      page_title: payload.page_title,
      page_referrer: payload.page_referrer,
    });
    if (Object.keys(pageContext).length > 0) {
      ReactGA.gtag("set", pageContext);
    }
  }

  trackPageView(params = {}) {
    this.setPageContext(params);
    this.track("page_view", params);
  }

  trackError(params = {}) {
    this.track("application_error", params, { dedupe: false });
  }

  trackPerformance(params = {}) {
    this.track("performance_metric", params);
  }

  trackAPI(params = {}) {
    this.track("api_response", params, { dedupe: false });
  }

  getSessionId() {
    return this.sessionId;
  }
}

const analyticsService = new AnalyticsService();

export default analyticsService;
